/**
 * «Gastos»: which expenses the list shows, in what order, and what they come to.
 *
 * The list used to be the newest hundred with the voided ones left out: anything older could not
 * be found at all, and a voided expense, which the books keep beside its correction, vanished
 * from the one screen that lists expenses. Pure, so the screen and its tests share one answer.
 */
import type { Centavos } from '@abastece/shared';
import type { CategoriaGasto, FormaPago, Gasto, PagoProveedor } from '@abastece/core';
import { rangoDe, type Periodo, type Rango } from './historial.js';
import { coincide } from './texto.js';
import type { Clave } from '../i18n/index.js';

/** How an expense is paid, as its form offers it: on credit, it is owed. */
export type FormaGasto = 'efectivo_cordobas' | 'efectivo_dolares' | 'transferencia' | 'billetera' | 'credito';

export const FORMAS_GASTO: { valor: FormaGasto; nombre: Clave }[] = [
  { valor: 'efectivo_cordobas', nombre: 'pago.efectivo_cordobas' },
  { valor: 'efectivo_dolares', nombre: 'gastos.efectivoUsd' },
  { valor: 'transferencia', nombre: 'pago.transferencia' },
  { valor: 'billetera', nombre: 'gastos.billeteraMovil' },
  { valor: 'credito', nombre: 'gastos.credito' },
];

/**
 * The screen's tabs, a job each (the owner, 2026-10-06: «every function is stacked vertically when
 * there really should be a tab for each function»): what was spent, what is still owed to
 * suppliers, what they were paid, and the owner's own money.
 */
export type VistaGastos = 'gastos' | 'porPagar' | 'pagos' | 'dueno';
export const VISTAS_GASTOS: readonly VistaGastos[] = ['gastos', 'porPagar', 'pagos', 'dueno'];

/**
 * What the address keeps of the screen. The tab, the first left out, so a reload, the phone's back
 * button, or «Volver» from the whole list of payments comes back to the tab that was open; and
 * `nuevo`, the home screen's «Nuevo gasto» asking for the form, which lives on the first tab. The
 * screen sanitizes with this too: the root route passes every parameter through (`busquedaDeAnalisis`).
 */
export function busquedaDeGastos(s: Record<string, unknown>): { nuevo?: true; vista?: VistaGastos } {
  if (s['nuevo'] === true) return { nuevo: true };
  const vista = VISTAS_GASTOS.find((v) => v === s['vista']);
  return vista && vista !== 'gastos' ? { vista } : {};
}

/**
 * The days are the sales' and the purchases' (`lib/historial.ts`): the periods with a name, as
 * «Contabilidad» has them, and two dates picked on a calendar (the owner, 2026-10-06).
 */
export interface FiltroGastos {
  periodo: Periodo;
  /** `YYYY-MM-DD`, for `elegir` only; either may be empty, for no bound on that side. */
  desde: string;
  hasta: string;
  categoria: CategoriaGasto | '';
  /** Who was paid; empty for anybody. */
  proveedorId: string;
  /** Empty for any; on credit is `credito`. */
  forma: FormaPago | '';
  texto: string;
  orden: 'fecha' | 'monto';
  conAnulados: boolean;
}

/** This month, newest first, voided ones out of the way. */
export const filtroInicial = (): FiltroGastos => ({
  periodo: 'mes',
  desde: '',
  hasta: '',
  categoria: '',
  proveedorId: '',
  forma: '',
  texto: '',
  orden: 'fecha',
  conAnulados: false,
});

/** Whether anything but the words differs from how the list opens: «Quitar los filtros» shows then. */
export const filtrosPuestos = (f: FiltroGastos): boolean => {
  const inicial = filtroInicial();
  return (
    f.periodo !== inicial.periodo ||
    f.categoria !== '' ||
    f.proveedorId !== '' ||
    f.forma !== '' ||
    f.orden !== inicial.orden ||
    f.conAnulados
  );
};

/** The days a filter covers, both ends included, or null for no bound. */
export const rango = (f: Pick<FiltroGastos, 'periodo' | 'desde' | 'hasta'>, hoy: string): Rango => rangoDe(f.periodo, hoy, f);

/**
 * The parts an expense was paid in that stand (`conElGasto`, 2026-10-16: «We need the option to pay
 * with mixed methods of payment»), each in córdobas: one voided on its own, of one on credit, was
 * not paid after all.
 */
export const partesDelGasto = (g: Pick<Gasto, 'pagos'>): PagoProveedor[] => (g.pagos ?? []).filter((x) => x.conElGasto && !x.anulado);

/**
 * How an expense was paid, as the filter takes it: each part paid with it, and its own way for what
 * the parts left, which is always something. Voided, it was still paid that way, as a purchase's
 * (`formasDeLaCompra`); a part voided on its own was not paid at all.
 */
export function formasDelGasto(g: Gasto): FormaPago[] {
  const partes = (g.pagos ?? []).filter((x) => x.conElGasto && (!x.anulado || x.anuladoConElGasto));
  return [...new Set<FormaPago>([...partes.map((x) => x.formaPago), g.formaPago])];
}

/**
 * What the drawer paid of an expense, in córdobas: the rest when it came out of one, and each part
 * that did. What its void puts back, while that till is open.
 */
export function deLaGavetaDelGasto(g: Gasto): number {
  const partes = partesDelGasto(g);
  const resto = partes.reduce((a, x) => a - x.monto, g.montoCordobas as number);
  return partes.reduce((a, x) => a + (x.sesionId ? x.monto : 0), g.sesionId ? resto : 0);
}

/** What a search looks in: what it was for, who was paid, the receipt's number, the note. */
function textoDe(g: Gasto, proveedor: string | undefined): string {
  return [g.descripcion, proveedor ?? '', g.referencia ?? '', g.nota ?? ''].join(' ');
}

export function filtrarGastos(
  gastos: readonly Gasto[],
  f: FiltroGastos,
  hoy: string,
  nombreProveedor: (id: string) => string | undefined,
): Gasto[] {
  const { desde, hasta } = rango(f, hoy);
  const texto = f.texto.trim();
  const reciente = (a: Gasto, b: Gasto) => b.fecha.localeCompare(a.fecha) || b.rev - a.rev;
  return gastos
    .filter(
      (g) =>
        (f.conAnulados || !g.anulado) &&
        (desde === null || g.fecha >= desde) &&
        (hasta === null || g.fecha <= hasta) &&
        (!f.categoria || g.categoria === f.categoria) &&
        (!f.proveedorId || g.proveedorId === f.proveedorId) &&
        (!f.forma || formasDelGasto(g).includes(f.forma)) &&
        (!texto || coincide(textoDe(g, g.proveedorId ? nombreProveedor(g.proveedorId) : undefined), texto)),
    )
    .sort(f.orden === 'monto' ? (a, b) => b.montoCordobas - a.montoCordobas || reciente(a, b) : reciente);
}

/** What the expenses shown came to. The voided ones are on the list, never in the sum. */
export function sumaGastos(gastos: readonly Gasto[]): { total: Centavos; cuantos: number } {
  const vigentes = gastos.filter((g) => !g.anulado);
  return { total: vigentes.reduce((a, g) => a + g.montoCordobas, 0) as Centavos, cuantos: vigentes.length };
}

/**
 * Whether an expense can still be corrected or voided: not once voided, and not once an expense
 * on credit has payments against it, which the books refuse (core `gastos.ts`). Its own parts are
 * paid with it, not against it: they go back with it.
 */
export const sePuedeCorregir = (g: Gasto): boolean =>
  !g.anulado && !(g.formaPago === 'credito' && (g.pagado ?? 0) > partesDelGasto(g).reduce((a, x) => a + x.monto, 0));

/**
 * Which of an expense's voids took, oldest first in `anulaciones`. A refused void stays in the log,
 * and after a sync a device cannot always tell it was refused; the books keep the reason of the
 * one that took and refuse any after it. So: the one with that reason, or failing that the last.
 */
export function anulacionQueQuedo<T extends { motivo: string }>(
  g: Pick<Gasto, 'anulado' | 'motivoAnulacion'>,
  anulaciones: readonly T[],
): T | undefined {
  if (!g.anulado) return undefined;
  return anulaciones.find((a) => a.motivo === g.motivoAnulacion) ?? anulaciones.at(-1);
}

/**
 * In what order a correction goes in.
 *
 * The new record first, then the old one voided naming it: if anything refuses the new one, the
 * old one stands and nothing is lost. Out of an open drawer, though, the new record is checked
 * against what the drawer holds with the old amount still out of it, so on a drawer with little
 * left even a correction downward would be refused. Then the old one is voided first, which puts
 * its amount back, and the new one goes in after it — not naming each other, since a void can
 * only name a record already there. Past what the drawer holds even with the old amount back, it
 * is not tried at all.
 *
 * `enGaveta` is what the drawer is expected to hold, when the correction comes out of one; all
 * three amounts in córdobas.
 */
export function ordenDeCorreccion(
  nuevo: number,
  original: number,
  enGaveta: number | null,
): 'nuevoPrimero' | 'anularPrimero' | 'noAlcanza' {
  if (enGaveta === null || nuevo <= enGaveta) return 'nuevoPrimero';
  return nuevo <= enGaveta + original ? 'anularPrimero' : 'noAlcanza';
}

/**
 * The till a correction pays from: the original's, while that till is open and the correction is
 * still cash in córdobas. Its void puts the old amount back in that drawer and the new record
 * takes the new amount out, so the drawer ends short by the difference only. From a till counted
 * since, both go through general cash, where a void after the corte already puts the money. Paid in
 * parts, the till is the one any of it came out of: one till to an expense.
 */
export function cajaDeCorreccion(
  original: Gasto,
  formaPago: string,
  abierta: (sesionId: string) => boolean,
): string | undefined {
  const sesionId = original.sesionId ?? partesDelGasto(original).find((x) => x.sesionId)?.sesionId ?? null;
  return sesionId && formaPago === 'efectivo_cordobas' && abierta(sesionId) ? sesionId : undefined;
}

/**
 * Whether a correction moves money: another amount, currency, way of paying, bank account or day. Once a count
 * found the money since the expense (the books refuse to void it, `sinContar`), that count found
 * the money as it really was, and a correction that moved it would move it a second time: only
 * what it was for can be put right then.
 */
export function correccionMueveDinero(antes: ComoSePago, despues: ComoSePago): boolean {
  const partes = (x: ComoSePago) => JSON.stringify((x.partes ?? []).map((p) => [p.formaPago, p.monto, p.cuenta ?? null]));
  return (
    antes.monto !== despues.monto ||
    antes.moneda !== despues.moneda ||
    antes.formaPago !== despues.formaPago ||
    antes.fecha !== despues.fecha ||
    (antes.cuenta ?? null) !== (despues.cuenta ?? null) ||
    partes(antes) !== partes(despues)
  );
}

/**
 * What an expense paid, how and when, and from which bank account, and its parts, each its way, its
 * amount and its account; an amount null while it does not read as one.
 */
interface ComoSePago {
  fecha: string;
  monto: number | null;
  moneda: string;
  formaPago: string;
  cuenta?: string | null;
  partes?: readonly { formaPago: string; monto: number | null; cuenta?: string | null }[];
}

/**
 * A supplier's payment as its debt keeps it, if it named itself (`pagoId`), voided ones too:
 * undefined for one from before payments were named, which nothing can be checked against, and
 * null for one the debt does not keep. That one the books refused, though the log may say it was
 * taken: a pull marks every event it brings confirmed.
 */
export function pagoDeLaDeuda(
  pagoId: string | undefined,
  deuda: { pagos?: readonly PagoProveedor[] | undefined } | undefined,
): PagoProveedor | undefined | null {
  if (!pagoId) return undefined;
  return deuda?.pagos?.find((x) => x.id === pagoId) ?? null;
}
